import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import useTranslation from '../../../hooks/useTranslations';
import { login, logout } from '../../../redux/modules/authSlice';
import { contentUpdate } from '../../../redux/modules/contentSlice';
import { selectMe } from '../../../redux/modules/data/userSlice';
import { ArrowIcon, CloseIcon, FeedbackIcon, GlobeIcon, LogoutIcon, RecentlyViewedIcon, RefreshIcon, UserIcon } from '../../misc/icons/Icons';


const UserMenu = ({ setIsFeedbackModalOpen, setIsLanguageModalOpen }) => {
	const t = useTranslation();
	const history = useHistory();
	const dispatch = useDispatch();
	const me = useSelector((state) => selectMe(state));

	const handleLogout = () => {
		history.push('/logout');
		dispatch(logout());
	};

	const handleContentUpdate = () => {
		// TODO remove dirty parallel calls
		dispatch(login());
		dispatch(contentUpdate());
	};

	const pushRoute = (e, route) => {
		e.preventDefault();
		history.push(route);
	};

	return (
		<Menu as="div" className="flex text-oly-blue-dark lg:mr-16 text-[1.0625rem]">
			{({ open }) => (
				<>

					<div>
						<Menu.Button
							className={`flex flex-row w-full px-3 py-3 text-[1.0625rem] font-semibold text-left transition duration-150 ease-in-out lg:py-2 focus:outline-none ${open ? 'lg:ring-2 lg:ring-oly-blue-dark bg-gray-50' : 'can-hover:lg:hover:ring-2 can-hover:lg:hover:ring-oly-blue-dark can-hover:hover:bg-gray-50 lg:focus-visible:ring-2 lg:focus-visible:ring-oly-blue-dark focus-visible:bg-gray-50'}`}
						>
							<UserIcon className="w-6 h-6 lg:mr-2" />
							<span className="hidden lg:inline">{t('webapp_topbar_profile')}</span>
							<ArrowIcon className={`self-center hidden w-5 h-5 ml-2 duration-300 transform lg:block ${open ? '-rotate-180' : 'rotate-0'}`} />
						</Menu.Button>
					</div>
					<Transition
						as={Fragment}
						enter="transition ease-out duration-100"
						enterFrom="transform opacity-0 scale-95"
						enterTo="transform opacity-100 scale-100"
						leave="transition ease-in duration-75"
						leaveFrom="transform opacity-100 scale-100"
						leaveTo="transform opacity-0 scale-95"
					>
						<Menu.Items className="absolute right-0 origin-top-right divide-y shadow-2xl top-12 lg:top-24 bg-oly-gray-lightestAlpha45 divide-oly-gray-normal w-68 ring-1 ring-black ring-opacity-5 focus:outline-none">
							<div className="px-1.5 lg:py-6">
								<Menu.Item>
									<button
										type="button"
										className="flex flex-row ml-auto p-3 text-[1.0625rem] font-semibold text-left transition duration-150 ease-in-out text-oly-blue-dark focus:outline-none"
										aria-labelledby="open-sidebar"
									>
										<CloseIcon className="w-6 h-6" />
									</button>
								</Menu.Item>
							</div>
							<div className="px-6 border-none lg:pb-9">
								<Menu.Item>
									<button
										type="button"
										className="flex items-center justify-between w-full px-2 py-1 mb-6 font-semibold text-left text-white group bg-oly-blue-default can-hover:hover:bg-oly-blue-default-dark focus-visible:bg-oly-blue-default-dark focus:outline-none"
										aria-labelledby="open-recently-viewed"
										onClick={(e) => pushRoute(e, '/recently-viewed')}
									>
										{t('dashboard_recently_used_button')}
										<RecentlyViewedIcon className="w-10 h-10" />
									</button>
								</Menu.Item>
								<Menu.Item>
									<button
										type="button"
										onClick={() => setIsFeedbackModalOpen(true)}
										className="flex items-center justify-between w-full px-2 py-1 mb-6 font-semibold text-left text-white group bg-oly-blue-default can-hover:hover:bg-oly-blue-default-dark focus-visible:bg-oly-blue-default-dark focus:outline-none"
										aria-labelledby="open-feedback"
									>
										{t('dashboard_feedback_button')}
										<FeedbackIcon className="w-10 h-10" />
									</button>
								</Menu.Item>
								<Menu.Item>
									<button
										type="button"
										onClick={() => setIsLanguageModalOpen(true)}
										className="flex items-center justify-between w-full px-2 py-1 mb-6 font-semibold text-left text-white group bg-oly-blue-default can-hover:hover:bg-oly-blue-default-dark focus-visible:bg-oly-blue-default-dark focus:outline-none"
										aria-labelledby="open-language-switcher"
									>
										{t('ifu_filter_language')}
										<GlobeIcon className="w-9 h-9" />
									</button>
								</Menu.Item>
								<Menu.Item>
									<button
										type="button"
										onClick={() => handleContentUpdate()}
										className="flex items-center justify-between w-full px-2 py-1 mb-6 font-semibold text-left text-white group bg-oly-blue-default can-hover:hover:bg-oly-blue-default-dark focus-visible:bg-oly-blue-default-dark focus:outline-none"
										aria-labelledby="content-update"
									>
										{t('update_content_title')}
										<RefreshIcon className="w-9 h-9" />
									</button>
								</Menu.Item>
								{/* <Menu.Item>
									<button
										type="button"
										className="flex items-center justify-between w-full px-2 py-1 mb-6 font-semibold text-left text-white group bg-oly-blue-default can-hover:hover:bg-oly-blue-default-dark focus-visible:bg-oly-blue-default-dark focus:outline-none"
										aria-labelledby="open-help"
									>
										{t('help_headline')}
										<HelpIcon className="w-10 h-10" />
									</button>
								</Menu.Item> */}
							</div>
							<div className="px-6 py-5 lg:py-9">
								<div className="text-oly-gray-darkest">
									{`${me?.first_name ?? ''} ${me?.last_name ?? ''}`}
								</div>
								<div className="text-oly-gray-darkest">
									{`${me?.email ?? ''}`}
								</div>
								<Menu.Item>
									<button
										type="button"
										className="flex items-center justify-between w-full px-2 py-1 mt-5 font-semibold text-left text-white group bg-oly-blue-default can-hover:hover:bg-oly-blue-default-dark focus-visible:bg-oly-blue-default-dark focus:outline-none"
										aria-labelledby="logout"
										onClick={() => handleLogout()}
									>
										{t('webapp_topbar_logout')}
										<LogoutIcon className="w-10 h-10" />
									</button>
								</Menu.Item>
							</div>
						</Menu.Items>
					</Transition>
				</>
			)}
		</Menu>
	);
};

export default UserMenu;
